import {
  createCreationFieldInitialValue,
  type CreateFieldOptions,
  type CreateTargetStatusOptions,
  type CreationValues,
} from '@cims-online/shared'
import { Alert, Form, Select } from 'antd'
import PayItemSelectProvider, {
  usePayItemSelectContext,
} from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CreateWorkflowRecordFormValues,
  validateCreationValues,
} from '../../validation'
import CreationField from '../CreationField'

export const CREATE_WORKFLOW_RECORD_FORM_ID = 'create-workflow-record-form'

type CreateWorkflowRecordFormProps = {
  onSubmit: (
    values: CreationValues,
    targetStatus: CreateTargetStatusOptions['targetStatus']
  ) => Promise<void>
  onTargetChange: (targetStatus: CreateTargetStatusOptions) => void
  onValidityChange: (valid: boolean) => void
  submitting: boolean
  targetStatuses: CreateTargetStatusOptions[]
}

function targetStatusKey({ targetStatus }: CreateTargetStatusOptions): string {
  return targetStatus.type === 'draft'
    ? 'draft'
    : `status:${targetStatus.statusId}`
}

function compareFields(
  first: CreateFieldOptions,
  second: CreateFieldOptions
): number {
  const firstRequired = first.required !== false
  const secondRequired = second.required !== false
  if (firstRequired !== secondRequired) return firstRequired ? -1 : 1

  const nameComparison = first.name.localeCompare(second.name)
  return nameComparison || first.id.localeCompare(second.id)
}

/** Whether initialization needs the asynchronous project pay-item options. */
function waitsForPayItemOptions(field: CreateFieldOptions): boolean {
  return (
    field.type === 'pay_items' &&
    field.initialValueTransformers.some(
      (transformer) => transformer.enabled && transformer.value === 'all'
    )
  )
}

/** Owns target selection, initialized values, and shared-schema validation. */
function CreateWorkflowRecordFormContent({
  onSubmit,
  onTargetChange,
  onValidityChange,
  submitting,
  targetStatuses,
}: CreateWorkflowRecordFormProps) {
  const { loading: payItemsLoading, payItems } = usePayItemSelectContext()
  const activePayItemIds = useMemo(
    () =>
      payItems
        .filter(({ deleted }) => !deleted)
        .map(({ globalId }) => globalId),
    [payItems]
  )
  const [form] = Form.useForm<CreateWorkflowRecordFormValues>()
  const values = useRef<CreateWorkflowRecordFormValues>({})
  const pendingInitializers = useRef<Record<string, CreateFieldOptions>>({})
  const [selectedTargetKey, setSelectedTargetKey] = useState<string>()
  const selectedTarget = targetStatuses.find(
    (targetStatus) => targetStatusKey(targetStatus) === selectedTargetKey
  )
  const fields = selectedTarget
    ? [...selectedTarget.fields].sort(compareFields)
    : []
  const [validation, setValidation] = useState<
    ReturnType<typeof validateCreationValues>
  >({ data: undefined, fieldIssues: {}, formIssues: [], valid: false })
  const [visibleFieldIssues, setVisibleFieldIssues] = useState(
    () => new Set<string>()
  )
  const [showFormIssues, setShowFormIssues] = useState(false)

  const validate = useCallback(() => {
    if (!selectedTarget) {
      onValidityChange(false)
      return undefined
    }

    const result = validateCreationValues(selectedTarget.fields, values.current)
    const hasPendingField = selectedTarget.fields.some(({ id }) =>
      Object.prototype.hasOwnProperty.call(pendingInitializers.current, id)
    )
    setValidation(result)
    onValidityChange(result.valid && !hasPendingField)
    return result
  }, [onValidityChange, selectedTarget])

  useEffect(() => {
    if (!payItemsLoading) {
      for (const [id, field] of Object.entries(pendingInitializers.current)) {
        if (!Object.prototype.hasOwnProperty.call(values.current, id))
          values.current[id] = createCreationFieldInitialValue(
            field,
            activePayItemIds
          )
        delete pendingInitializers.current[id]
      }
    }

    if (!selectedTarget) {
      validate()
      return
    }

    for (const field of selectedTarget.fields) {
      if (
        Object.prototype.hasOwnProperty.call(values.current, field.id) ||
        Object.prototype.hasOwnProperty.call(
          pendingInitializers.current,
          field.id
        )
      )
        continue
      if (payItemsLoading && waitsForPayItemOptions(field)) {
        pendingInitializers.current[field.id] = field
        continue
      }
      values.current[field.id] = createCreationFieldInitialValue(
        field,
        activePayItemIds
      )
    }
    form.setFields(
      selectedTarget.fields.map(({ id }) => ({
        name: id,
        value: values.current[id],
      }))
    )
    validate()
  }, [activePayItemIds, form, payItemsLoading, selectedTarget, validate])

  const handleSubmit = useCallback(async () => {
    if (submitting || !selectedTarget) return

    const result = validate()
    if (!result) return

    const hasPendingField = selectedTarget.fields.some(({ id }) =>
      Object.prototype.hasOwnProperty.call(pendingInitializers.current, id)
    )
    if (!result.valid || !result.data || hasPendingField) {
      setVisibleFieldIssues(new Set(Object.keys(result.fieldIssues)))
      setShowFormIssues(true)
      return
    }

    await onSubmit(result.data, selectedTarget.targetStatus)
  }, [onSubmit, selectedTarget, submitting, validate])

  return (
    <Form<CreateWorkflowRecordFormValues>
      form={form}
      id={CREATE_WORKFLOW_RECORD_FORM_ID}
      layout="vertical"
      onFinish={handleSubmit}
      onValuesChange={(changedValues: CreateWorkflowRecordFormValues) => {
        if (!selectedTarget) return
        for (const { id } of selectedTarget.fields) {
          if (Object.prototype.hasOwnProperty.call(changedValues, id)) {
            values.current[id] = changedValues[id]
            delete pendingInitializers.current[id]
          }
        }
        validate()
      }}
    >
      <Form.Item colon label="Send to">
        <Select
          aria-label="Send to"
          onChange={(key: string) => {
            const target = targetStatuses.find(
              (targetStatus) => targetStatusKey(targetStatus) === key
            )
            if (!target) return

            setSelectedTargetKey(key)
            onTargetChange(target)
            setVisibleFieldIssues(new Set())
            setShowFormIssues(false)
          }}
          options={targetStatuses.map((targetStatus) => ({
            label: targetStatus.label,
            value: targetStatusKey(targetStatus),
          }))}
          placeholder="Select a status"
          style={{ width: '100%' }}
          value={selectedTargetKey}
        />
      </Form.Item>
      {showFormIssues && validation.formIssues.length > 0 && (
        <Alert
          message={validation.formIssues.join(' ')}
          showIcon
          type="error"
        />
      )}
      {fields.map((field) => (
        <CreationField
          error={
            visibleFieldIssues.has(field.id)
              ? validation.fieldIssues[field.id]?.join(' ')
              : undefined
          }
          field={field}
          key={field.id}
          onBlur={() => {
            setVisibleFieldIssues((visible) => new Set(visible).add(field.id))
            validate()
          }}
        />
      ))}
    </Form>
  )
}

/** Provides project pay-item options to the creation form and its controls. */
function CreateWorkflowRecordForm(props: CreateWorkflowRecordFormProps) {
  return (
    <PayItemSelectProvider>
      <CreateWorkflowRecordFormContent {...props} />
    </PayItemSelectProvider>
  )
}

export default CreateWorkflowRecordForm
